import React from 'react';
import { Box, Toolbar, Typography, ListItem, ListItemIcon, ListItemText, Tooltip, IconButton, Divider, Badge } from '@mui/material';
import { Link, useLocation } from 'react-router-dom';
import { 
  TestTubeDiagonal, 
  FileText, 
  Star, 
  ListOrdered, 
  FileEdit,
  ImageIcon, 
  Settings,
  CreditCard,
  Sparkles,
  PenLine, 
  Link2,
  ChevronLeft,
  ChevronRight,
  Facebook,
  Home,
  BrainCircuit,
  Zap
} from 'lucide-react';
import GlobalModelSelector from './GlobalModelSelector';

const DrawerContent = ({ isGenerating = false, isCollapsed, setIsCollapsed }) => {
  const location = useLocation();
  
  const isCurrentPath = (path) => location.pathname === path;

  const iconColors = {
    '/': '#E04C16',
    '/articles/quick': '#FF6B3D',
    '/articles/single': '#3B82F6',
    '/articles/standard': '#10B981',
    '/articles/reviews': '#F59E0B',
    '/articles/lists': '#8B5CF6',
    '/url-rewriter': '#4F46E5',
    '/articles/rewrites': '#6B7280',
    '/prompts/new': '#E04C16',
    '/prompts/saved': '#14B8A6',
    '/social/facebook': '#2563EB',
    '/social/facebookideas': '#EA580C',
    '/images': '#EC4899',
    '/subscription': '#059669',
    '/dashboard': '#4B5563',
  };

  const MenuLink = ({ to, icon, text, indent = false, isNew = false }) => (
    <Tooltip 
      title={isCollapsed ? text : (isGenerating ? "Please wait until article generation is complete" : "")}
      placement="right"
    >
      <ListItem
        component={Link}
        to={to}
        disabled={isGenerating}
        onClick={(e) => {
          if (isGenerating) {
            e.preventDefault();
          }
        }}
        sx={{
          pl: isCollapsed ? 2 : 3,  
          py: 0.8,
          my: 0.2,
          color: isGenerating ? 'text.disabled' : '#4B5563',
          textDecoration: 'none',
          borderRadius: '10px',
          mr: 1,
          position: 'relative',
          backgroundColor: isCurrentPath(to) ? 'rgba(255, 255, 255, 0.9)' : 'transparent',
          boxShadow: isCurrentPath(to) ? '0 4px 12px rgba(0, 0, 0, 0.08)' : 'none',
          '&:before': isCurrentPath(to) ? {
            content: '""',
            position: 'absolute',
            left: 0,
            top: '50%',
            transform: 'translateY(-50%)',
            width: '3px',
            height: '60%',
            borderRadius: '0 4px 4px 0',
            backgroundColor: iconColors[to] || '#E04C16',
          } : {},
          '&:hover': {
            backgroundColor: isGenerating ? 'transparent' : (isCurrentPath(to) ? 'rgba(255, 255, 255, 0.95)' : 'rgba(255, 255, 255, 0.7)'),
            transform: isGenerating ? 'none' : 'translateX(2px)',
            boxShadow: isGenerating ? 'none' : (isCurrentPath(to) ? '0 4px 12px rgba(0, 0, 0, 0.12)' : '0 2px 8px rgba(0, 0, 0, 0.05)'),
          },
          pointerEvents: isGenerating ? 'none' : 'auto',
          opacity: isGenerating ? 0.5 : 1,
          transition: 'all 0.2s ease-in-out',
          minWidth: 0,  
          whiteSpace: 'nowrap',  
          overflow: 'hidden',
        }}
      >
        <ListItemIcon sx={{ 
          minWidth: isCollapsed ? 0 : 32,
          justifyContent: 'center',
          '& svg': {
            filter: isCurrentPath(to) ? 'drop-shadow(0 0 3px rgba(0, 0, 0, 0.1))' : 'none',
            transition: 'all 0.2s ease'
          }
        }}>
          {React.cloneElement(icon, { 
            size: 18,
            color: isGenerating ? '#999999' : (iconColors[to] || '#E04C16'),
            strokeWidth: 1.5,
          })}
        </ListItemIcon>
        {!isCollapsed && (
          <ListItemText
            primary={text}
            primaryTypographyProps={{
              variant: 'body2',
              sx: {
                fontSize: '0.85rem',
                fontWeight: isCurrentPath(to) ? 600 : 500,  
                color: isGenerating ? 'text.disabled' : (isCurrentPath(to) ? '#1F2937' : '#4B5563'),
                letterSpacing: '0.01em',
                fontFamily: 'Inter, system-ui, sans-serif',
              }
            }}
          />
        )}
        {!isCollapsed && isNew && (
          <Badge
            sx={{
              '& .MuiBadge-badge': {
                fontSize: '0.6rem',
                height: '16px',
                minWidth: '16px',
                padding: '0 4px',
                backgroundColor: '#10B981',
                color: 'white',
                fontWeight: 600,
                textTransform: 'uppercase',
                letterSpacing: '0.02em',
                boxShadow: '0 2px 4px rgba(16, 185, 129, 0.3)'
              }
            }}
            badgeContent="New"
          />
        )}
      </ListItem>
    </Tooltip>
  );

  const SectionTitle = ({ text }) => {
    if (isCollapsed) return null;
    return (
      <Typography
        variant="overline"
        sx={{
          pl: 3,
          mb: 0.5,
          mt: 1.5,
          display: 'block',
          color: isGenerating ? '#999999' : '#6B7280',
          fontSize: '0.65rem',
          fontWeight: 600,
          letterSpacing: '0.08em',
          textTransform: 'uppercase',
          fontFamily: 'Inter, system-ui, sans-serif',
          opacity: 0.8,
        }}
      >
        {text}
      </Typography>
    );
  };

  const CompactModelSelector = () => {
    if (isCollapsed) return null;
    
    return <GlobalModelSelector compact={true} />;
  };

  return (
    <Box sx={{ 
      height: '100vh', 
      display: 'flex', 
      flexDirection: 'column',
      position: 'relative',
      width: '100%',  
      overflowX: 'hidden',
      background: 'linear-gradient(180deg, #F8FAFC 0%, #F1F5F9 100%)',
      boxShadow: '0 0 20px rgba(0, 0, 0, 0.05)',
    }}>
      
      {/* Collapse Toggle Button */}
      <IconButton
        onClick={() => setIsCollapsed(!isCollapsed)}
        sx={{
          position: 'absolute',
          right: 8,  
          top: '50%',
          transform: 'translate(50%, -50%)',  
          backgroundColor: '#FFFFFF',
          border: '1px solid #E5E7EB',
          width: 24,
          height: 24,
          padding: 0,
          zIndex: 1200,
          boxShadow: '0 2px 8px rgba(0, 0, 0, 0.08)',
          '&:hover': {
            backgroundColor: '#F8FAFC',
            borderColor: '#D1D5DB',
            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.12)',
          },
          transition: 'all 0.2s ease-in-out',
        }}
      >
        {isCollapsed ? 
          <ChevronRight size={14} color="#6B7280" strokeWidth={1.5} /> :
          <ChevronLeft size={14} color="#6B7280" strokeWidth={1.5} />
        }
      </IconButton>

      {/* Logo Section */}
      <Toolbar sx={{ 
        px: 2,
        justifyContent: isCollapsed ? 'center' : 'flex-start',
        minHeight: '60px !important',
        position: 'relative',
        borderBottom: '1px solid rgba(0, 0, 0, 0.06)',
      }}>
        <Box sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1.5,
          pl: isCollapsed ? 0 : 0.5,
        }}>
          <Box sx={{
            width: 32,
            height: 32,
            borderRadius: '10px',
            background: 'linear-gradient(135deg, #E04C16 0%, #8B5CF6 50%, #3B82F6 100%)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            boxShadow: '0 4px 10px rgba(224, 76, 22, 0.3)',
          }}>
            <Sparkles 
              size={18} 
              color="#FFFFFF"
              strokeWidth={1.5}
              className="animate-pulse-sparkle"
            />
          </Box>
          {!isCollapsed && (
            <Box sx={{ position: 'relative' }}>
              <Typography
                variant="h6"
                sx={{
                  fontFamily: 'Inter, system-ui, sans-serif',
                  fontSize: '1.6rem',
                  fontWeight: 800,
                  letterSpacing: '-0.02em',
                  color: '#8B5CF6',
                }}
              >
                Scripti
              </Typography>
              <Box
                sx={{
                  position: 'absolute',
                  top: '55%',
                  right: -42,
                  backgroundColor: 'rgba(59, 130, 246, 0.1)',
                  color: '#3B82F6',
                  fontSize: '0.55rem',
                  padding: '1px 5px',
                  borderRadius: '8px',
                  fontWeight: 600,
                  letterSpacing: '0.02em',
                  transform: 'translateY(-50%)',
                  fontFamily: 'Inter, system-ui, sans-serif',
                  boxShadow: 'none',
                }}
              >
                v1.02
              </Box>
            </Box>
          )}
        </Box>
      </Toolbar>

      {/* Menu Items */}
      <Box sx={{ 
        flex: 1, 
        display: 'flex',
        flexDirection: 'column',
        px: 1.5,
        py: 1,
        overflowY: 'auto',
        '&::-webkit-scrollbar': {
          width: '3px',
        },
        '&::-webkit-scrollbar-track': {
          background: 'transparent',
        },
        '&::-webkit-scrollbar-thumb': {
        
          borderRadius: '3px',
        },
      }}>
        {!isCollapsed && (
          <MenuLink to="/" icon={<Home />} text="Home" />
        )}
        
        <SectionTitle text="CONTENT CREATION" />
        <MenuLink to="/articles/quick" icon={<Zap />} text="Quick Article Creator" isNew={true} />
        <MenuLink to="/articles/single" icon={<FileText />} text="Single Article Creator" />
        <MenuLink to="/articles/standard" icon={<FileText />} text="Bulk Article Creator" />
        <MenuLink to="/articles/reviews" icon={<Star />} text="Product Reviews" />
        <MenuLink to="/articles/lists" icon={<ListOrdered />} text="Listicle Articles" />
        <MenuLink to="/url-rewriter" icon={<Link2 />} text="URL Rewriter" /> 
        <MenuLink to="/articles/rewrites" icon={<FileEdit />} text="Post Rewriter" />
        
        <SectionTitle text="PROMPT LAB" />
        <MenuLink to="/prompts" icon={<TestTubeDiagonal />} text="Prompt Lab" />
        
        
        <SectionTitle text="IMAGE TOOLS" />
        <MenuLink to="/images" icon={<ImageIcon />} text="Image Generator" />
        <MenuLink to="/image-editor" icon={<ImageIcon />} text="Image Editor" isNew={true} />

        <SectionTitle text="SOCIAL CONTENT" />
       
        <MenuLink to="/social/facebookideas" icon={<Facebook />} text="Facebook Ideas" isNew={true} />
        
      </Box>

      {/* Bottom Section */}
      <Box sx={{ 
        pt: 0,
        pb: 1.5,
        px: 1.5,
        mt: 'auto',
        opacity: isGenerating ? 0.5 : 1,
        pointerEvents: isGenerating ? 'none' : 'auto',
        backgroundColor: 'transparent',
      }}>
        {!isCollapsed && <CompactModelSelector />}
        <MenuLink to="/subscription" icon={<CreditCard />} text="Subscription" />
        <MenuLink to="/dashboard" icon={<Settings />} text="Settings" />
      </Box>
    </Box>
  );
};

export default DrawerContent;